/* eslint-disable no-template-curly-in-string */
import React, { useRef } from 'react';
import { Box, FormHelperText, SxProps, Theme } from '@mui/material';
import Editor, { Monaco } from '@monaco-editor/react';
import * as monaco from 'monaco-editor';

// Track if LaTeX language has been registered
let latexLanguageRegistered = false;

interface LatexEditorProps {
  value: string;
  onChange: (value: string) => void;
  helperText?: string;
  placeholder?: string;
  disabled?: boolean;
  sx?: SxProps<Theme>;
  height?: string | number;
}

const LatexEditor: React.FC<LatexEditorProps> = ({
  value,
  onChange,
  helperText,
  placeholder,
  disabled = false,
  sx = {},
  height = 600,
}) => {
  const editorRef = useRef<monaco.editor.IStandaloneCodeEditor | null>(null);

  const handleEditorDidMount = (editor: monaco.editor.IStandaloneCodeEditor, monacoInstance: Monaco) => {
    editorRef.current = editor;

    // Register LaTeX as a custom language (only once)
    if (!latexLanguageRegistered) {
      latexLanguageRegistered = true;

      monacoInstance.languages.register({ id: 'latex' });

      // Define LaTeX syntax highlighting
      monacoInstance.languages.setMonarchTokensProvider('latex', {
      tokenizer: {
        root: [
          // Comments
          [/%.*$/, 'comment'],

          // Commands
          [/\\[a-zA-Z]+/, 'keyword'],

          // Curly braces
          [/[{}]/, 'delimiter.curly'],

          // Square brackets
          [/[[\]]/, 'delimiter.square'],

          // Math mode delimiters
          [/\$\$/, 'string.math'],
          [/\$/, 'string.math'],
          [/\\[([]/, 'string.math'],
          [/\\[)\]]/, 'string.math'],

          // Pattern variables
          [/\{\{[a-zA-Z_][a-zA-Z0-9_]*\}\}/, 'variable'],

          // Numbers
          [/\d+(\.\d+)?/, 'number'],
        ],
      },
    });

    // Define LaTeX theme colors
    monacoInstance.editor.defineTheme('latex-theme', {
      base: 'vs',
      inherit: true,
      rules: [
        { token: 'comment', foreground: '008000', fontStyle: 'italic' },
        { token: 'keyword', foreground: '0000FF', fontStyle: 'bold' },
        { token: 'string.math', foreground: 'A31515' },
        { token: 'variable', foreground: 'FF00FF', fontStyle: 'bold' },
        { token: 'number', foreground: '098658' },
        { token: 'delimiter.curly', foreground: '000000', fontStyle: 'bold' },
        { token: 'delimiter.square', foreground: '000000' },
      ],
      colors: {},
    });

    // Configure editor options
    editor.updateOptions({
      fontSize: 14,
      fontFamily: 'Consolas, "Courier New", monospace',
      lineNumbers: 'on',
      minimap: {
        enabled: true,
        scale: 1,
      },
      scrollBeyondLastLine: false,
      automaticLayout: true,
      tabSize: 2,
      insertSpaces: true,
      wordWrap: 'on',
      folding: true,
      foldingStrategy: 'indentation',
      showFoldingControls: 'always',
      readOnly: disabled,
      scrollbar: {
        vertical: 'auto',
        horizontal: 'auto',
        verticalScrollbarSize: 10,
        horizontalScrollbarSize: 10,
      },
      renderLineHighlight: 'all',
      cursorBlinking: 'smooth',
      smoothScrolling: true,
      contextmenu: true,
      quickSuggestions: {
        other: true,
        comments: true,
        strings: true,
      },
      suggestOnTriggerCharacters: true,
      acceptSuggestionOnCommitCharacter: true,
      acceptSuggestionOnEnter: 'on',
      snippetSuggestions: 'inline',
      bracketPairColorization: {
        enabled: true,
      },
    });

    // Register LaTeX snippets for common commands
    monacoInstance.languages.registerCompletionItemProvider('latex', {
      provideCompletionItems: (model, position) => {
        const word = model.getWordUntilPosition(position);
        const range = {
          startLineNumber: position.lineNumber,
          endLineNumber: position.lineNumber,
          startColumn: word.startColumn,
          endColumn: word.endColumn
        };

        const suggestions: Omit<monaco.languages.CompletionItem, 'range'>[] = [
          // Math environments
          {
            label: 'frac',
            kind: monacoInstance.languages.CompletionItemKind.Snippet,
            insertText: '\\frac{${1:numerator}}{${2:denominator}}',
            insertTextRules: monacoInstance.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            documentation: 'Fraction',
          },
          {
            label: 'sqrt',
            kind: monacoInstance.languages.CompletionItemKind.Snippet,
            insertText: '\\sqrt{${1:expression}}',
            insertTextRules: monacoInstance.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            documentation: 'Square root',
          },
          {
            label: 'begin-end',
            kind: monacoInstance.languages.CompletionItemKind.Snippet,
            insertText: '\\begin{${1:environment}}\n\t$0\n\\end{${1:environment}}',
            insertTextRules: monacoInstance.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            documentation: 'Begin-End environment',
          },
          {
            label: 'align',
            kind: monacoInstance.languages.CompletionItemKind.Snippet,
            insertText: '\\begin{align}\n\t$0\n\\end{align}',
            insertTextRules: monacoInstance.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            documentation: 'Align environment',
          },
          {
            label: 'equation',
            kind: monacoInstance.languages.CompletionItemKind.Snippet,
            insertText: '\\begin{equation}\n\t$0\n\\end{equation}',
            insertTextRules: monacoInstance.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            documentation: 'Equation environment',
          },
          {
            label: 'itemize',
            kind: monacoInstance.languages.CompletionItemKind.Snippet,
            insertText: '\\begin{itemize}\n\t\\item $0\n\\end{itemize}',
            insertTextRules: monacoInstance.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            documentation: 'Itemize list',
          },
          {
            label: 'enumerate',
            kind: monacoInstance.languages.CompletionItemKind.Snippet,
            insertText: '\\begin{enumerate}\n\t\\item $0\n\\end{enumerate}',
            insertTextRules: monacoInstance.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            documentation: 'Enumerate list',
          },
          // Text formatting
          {
            label: 'textbf',
            kind: monacoInstance.languages.CompletionItemKind.Snippet,
            insertText: '\\textbf{${1:text}}',
            insertTextRules: monacoInstance.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            documentation: 'Bold text',
          },
          {
            label: 'textit',
            kind: monacoInstance.languages.CompletionItemKind.Snippet,
            insertText: '\\textit{${1:text}}',
            insertTextRules: monacoInstance.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            documentation: 'Italic text',
          },
          {
            label: 'underline',
            kind: monacoInstance.languages.CompletionItemKind.Snippet,
            insertText: '\\underline{${1:text}}',
            insertTextRules: monacoInstance.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            documentation: 'Underlined text',
          },
          // Sections
          {
            label: 'section',
            kind: monacoInstance.languages.CompletionItemKind.Snippet,
            insertText: '\\section{${1:title}}',
            insertTextRules: monacoInstance.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            documentation: 'Section',
          },
          {
            label: 'subsection',
            kind: monacoInstance.languages.CompletionItemKind.Snippet,
            insertText: '\\subsection{${1:title}}',
            insertTextRules: monacoInstance.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            documentation: 'Subsection',
          },
          // Math symbols
          {
            label: 'sum',
            kind: monacoInstance.languages.CompletionItemKind.Snippet,
            insertText: '\\sum_{${1:i=0}}^{${2:n}} ${3:expression}',
            insertTextRules: monacoInstance.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            documentation: 'Summation',
          },
          {
            label: 'int',
            kind: monacoInstance.languages.CompletionItemKind.Snippet,
            insertText: '\\int_{${1:a}}^{${2:b}} ${3:f(x)} \\, dx',
            insertTextRules: monacoInstance.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            documentation: 'Integral',
          },
          {
            label: 'lim',
            kind: monacoInstance.languages.CompletionItemKind.Snippet,
            insertText: '\\lim_{${1:x} \\to ${2:a}} ${3:f(x)}',
            insertTextRules: monacoInstance.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            documentation: 'Limit',
          },
          // Vectors and matrices
          {
            label: 'vec',
            kind: monacoInstance.languages.CompletionItemKind.Snippet,
            insertText: '\\vec{${1:v}}',
            insertTextRules: monacoInstance.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            documentation: 'Vector',
          },
          {
            label: 'matrix',
            kind: monacoInstance.languages.CompletionItemKind.Snippet,
            insertText: '\\begin{pmatrix}\n\t${1:a} & ${2:b} \\\\\\\n\t${3:c} & ${4:d}\n\\end{pmatrix}',
            insertTextRules: monacoInstance.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            documentation: 'Matrix (parentheses)',
          },
          {
            label: 'bmatrix',
            kind: monacoInstance.languages.CompletionItemKind.Snippet,
            insertText: '\\begin{bmatrix}\n\t${1:a} & ${2:b} \\\\\\\n\t${3:c} & ${4:d}\n\\end{bmatrix}',
            insertTextRules: monacoInstance.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            documentation: 'Matrix (brackets)',
          },
          // Custom variables (for patterns)
          {
            label: 'variable',
            kind: monacoInstance.languages.CompletionItemKind.Snippet,
            insertText: '{{${1:variable_name}}}',
            insertTextRules: monacoInstance.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            documentation: 'Pattern variable placeholder',
          },
        ];

        // Add range to all suggestions
        const suggestionsWithRange = suggestions.map(s => ({ ...s, range }));

        return { suggestions: suggestionsWithRange };
      },
    });

    // Add bracket matching configuration
    monacoInstance.languages.setLanguageConfiguration('latex', {
      brackets: [
        ['{', '}'],
        ['[', ']'],
        ['(', ')'],
      ],
      autoClosingPairs: [
        { open: '{', close: '}' },
        { open: '[', close: ']' },
        { open: '(', close: ')' },
        { open: '$', close: '$' },
        { open: '\\(', close: '\\)' },
        { open: '\\[', close: '\\]' },
      ],
      surroundingPairs: [
        { open: '{', close: '}' },
        { open: '[', close: ']' },
        { open: '(', close: ')' },
        { open: '$', close: '$' },
      ],
    });
    } // End of LaTeX language registration
  };

  const handleChange = (newValue: string | undefined) => {
    if (newValue !== undefined && newValue !== value) {
      onChange(newValue);
    }
  };

  return (
    <Box sx={{ ...sx, display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{
          border: '1px solid',
          borderColor: disabled ? 'action.disabledBackground' : 'divider',
          borderRadius: 1,
          overflow: 'hidden',
          backgroundColor: disabled ? '#f5f5f5' : '#ffffff',
          flexGrow: 1,
          height: height,
        }}
      >
        <Editor
          height="100%"
          defaultLanguage="latex"
          value={value}
          onChange={handleChange}
          onMount={handleEditorDidMount}
          theme="latex-theme"
          options={{
            readOnly: disabled,
          }}
          loading={
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
              }}
            >
              Chargement de l'éditeur LaTeX...
            </Box>
          }
        />
      </Box>
      {helperText && (
        <FormHelperText sx={{ ml: 2, mt: 0.5 }}>{helperText}</FormHelperText>
      )}
    </Box>
  );
};

export default LatexEditor;
